// Conditions and loop stop rules that test what an earlier AI (Decision) step
// produced, e.g. "Skip if: Assess the feedback → critical is false".
//
// Field names are `decision.<nodeId>.<outputKey>`. Only boolean, enum and
// number outputs can be tested: free text is for writing messages, not for
// branching on. The engine fills these from the run's Decision outputs; a
// Decision that produced nothing leaves them undefined, which never trips a
// rule (predicates fail open, see predicates.ts).

import type { ContextFieldDef } from "./context.ts";
import type {
  JsonValue,
  OutputField,
  WorkflowContext,
  WorkflowDefinition,
} from "./schema.ts";

export const DECISION_FIELD_PREFIX = "decision.";

export function decisionFieldKey(nodeId: string, outputKey: string): string {
  return `${DECISION_FIELD_PREFIX}${nodeId}.${outputKey}`;
}

/** { nodeId, key } for a decision field, or null for any other field. */
export function parseDecisionField(
  field: string,
): { nodeId: string; key: string } | null {
  if (!field.startsWith(DECISION_FIELD_PREFIX)) return null;
  const rest = field.slice(DECISION_FIELD_PREFIX.length);
  const dot = rest.lastIndexOf(".");
  if (dot <= 0 || dot === rest.length - 1) return null;
  return { nodeId: rest.slice(0, dot), key: rest.slice(dot + 1) };
}

function operatorsFor(output: OutputField): string[] | null {
  switch (output.type) {
    case "boolean":
      return ["is_true", "is_false"];
    case "enum":
      return ["eq", "neq"];
    case "number":
    case "money":
      return ["gt", "lte", "eq"];
    default:
      return null;
  }
}

/**
 * The testable outputs of every Decision ABOVE `beforeIndex`, as catalog
 * entries the builder's field picker and publish validation both use.
 */
export function decisionFieldDefs(
  def: WorkflowDefinition,
  beforeIndex: number,
): ContextFieldDef[] {
  const out: ContextFieldDef[] = [];
  def.nodes.slice(0, Math.max(0, beforeIndex)).forEach((node) => {
    if (node.type !== "decision") return;
    const outputs = (node.parameters.output as unknown as OutputField[]) ?? [];
    for (const output of outputs) {
      const operators = operatorsFor(output);
      if (!operators) continue;
      out.push({
        field: decisionFieldKey(node.id, output.key),
        label: `${node.label ?? "AI step"}: ${output.key.replace(/_/g, " ")}`,
        operators,
        valueHint: output.type === "enum"
          ? (output.enumValues ?? []).join(" / ")
          : output.type === "boolean"
          ? undefined
          : "number",
        description: output.description,
      });
    }
  });
  return out;
}

/** Adds the referenced decision fields from the run's Decision outputs. */
export function withDecisionFields(
  ctx: WorkflowContext,
  referenced: Iterable<string>,
  outputs: Record<string, Record<string, JsonValue>>,
): WorkflowContext {
  const next = { ...ctx };
  for (const field of referenced) {
    const parsed = parseDecisionField(field);
    if (!parsed) continue;
    const value = outputs[parsed.nodeId]?.[parsed.key];
    if (value !== undefined) next[field] = value;
  }
  return next;
}
